import { useMemo } from 'react';

import { EmptyImage } from 'app/components/commons/Images/EmptyImage/EmptyImage';
import { SingleImage } from 'app/components/commons/Images/SingleImage/SingleImage';
import { TripleImages } from 'app/components/commons/Images/TripleImages/TripleImages';
import { EXPERIENCE_STATUS } from 'app/typings/core/admin/experiences/enum/experienceStatus';
import { PackStatus } from 'app/typings/core/admin/packs/enum/pack';
import type { PictureView } from 'app/typings/core/common/pictureView/pictureView';
import { canApplyPackStatusAction } from 'app/utils/packStatus';

import type { HotelActivity, HotelPackage, HotelRoom } from '../_types';

import {
  getPackageNameForHotel,
  getPackageNameForUser,
  getPackagePath,
} from './_utils/packageUtils';
import { EntityRow } from './entity/EntityRow';
import { EntitySection } from './entity/EntitySection';
import { EntityTable } from './entity/EntityTable';
import type { EntityRowConfig, EntitySectionConfig } from './entity/entity';
import { SortableSection } from './sortable/SortableSection';

type Props = {
  daytimePackages: HotelPackage[];
  nightStayPackages: HotelPackage[];
  isLegacy: boolean;
  onCreatePackage: () => void;
  onPublish: (pkg: HotelPackage) => void;
  onSetup: (pkg: HotelPackage) => void;
  onUnpublish: (pkg: HotelPackage) => void;
  onHide: (pkg: HotelPackage) => void;
  onReorder?: (sectionKey: string, items: HotelPackage[]) => void;
  activities: HotelActivity[];
  rooms: HotelRoom[];
};

const COVER_IMAGE_SIZE = 40;

const isPictureView = (picture?: PictureView | null): picture is PictureView =>
  Boolean(picture);

const getPackageCoverImages = (
  pkg: HotelPackage,
  activities: HotelActivity[],
  rooms: HotelRoom[]
): PictureView[] => {
  const alwaysIncludedActivities = activities.filter(
    (activity) => activity.isAlwaysIncludedWithNight
  );
  const firstAlwaysIncluded = alwaysIncludedActivities[0];

  const getPackExperience = (activityId: number) =>
    pkg.packExperiences?.find(
      (packExperience) => packExperience.experienceId === activityId
    );

  const packageActivities = activities.filter((activity) =>
    Boolean(
      getPackExperience(activity.id)?.isIncluded &&
        activity.status === EXPERIENCE_STATUS.PUBLISHED
    )
  );

  const packageRooms = rooms.filter((room) =>
    pkg.packRoomIds?.some((packRoomId: number) => packRoomId === room.id)
  );

  packageActivities.sort((activityA, activityB) => {
    const priorityA = getPackExperience(activityA.id)?.priority ?? Infinity;
    const priorityB = getPackExperience(activityB.id)?.priority ?? Infinity;

    return priorityA - priorityB;
  });

  if (pkg.isDaytime) {
    return [packageActivities[0]?.coverPicture ?? null].filter(isPictureView);
  }

  const thirdImage = packageActivities[1]
    ? packageActivities[1]?.coverPicture
    : firstAlwaysIncluded?.coverPicture;

  return [
    packageActivities[0]?.coverPicture ?? null,
    packageRooms[0]?.pictures?.[0] ?? null,
    thirdImage ?? null,
  ].filter(isPictureView);
};

const getRowConfig = (
  pkg: HotelPackage,
  isLegacy: boolean,
  handlers: Pick<Props, 'onPublish' | 'onSetup' | 'onUnpublish' | 'onHide'>,
  activities: HotelActivity[],
  rooms: HotelRoom[]
): EntityRowConfig => {
  const images = getPackageCoverImages(pkg, activities, rooms);

  const getImageComponent = () => {
    if (images.length === 0) {
      return <EmptyImage width={COVER_IMAGE_SIZE} height={COVER_IMAGE_SIZE} />;
    }

    if (pkg.isDaytime) {
      return (
        <SingleImage
          image={images[0]}
          width={COVER_IMAGE_SIZE}
          height={COVER_IMAGE_SIZE}
        />
      );
    }

    return (
      <TripleImages
        images={images}
        width={COVER_IMAGE_SIZE}
        height={COVER_IMAGE_SIZE}
      />
    );
  };

  const imageComponent = getImageComponent();

  return {
    id: pkg.id,
    cover: imageComponent,
    primaryLabel: getPackageNameForHotel(pkg),
    pkgUserLabel: getPackageNameForUser(pkg),
    status: pkg.status,
    redirectPath: getPackagePath(pkg, isLegacy),
    actions: [
      pkg.status !== PackStatus.PUBLISHED && {
        key: 'publish',
        label: 'Publish',
        disabled: !canApplyPackStatusAction('publish', pkg.status, isLegacy),
        onClick: () => handlers.onPublish(pkg),
      },
      // Setup only exists in the new data model.
      !isLegacy &&
        pkg.status !== PackStatus.SETUP && {
          key: 'setup',
          label: 'Setup',
          disabled: !canApplyPackStatusAction('setup', pkg.status, isLegacy),
          onClick: () => handlers.onSetup(pkg),
        },
      pkg.status !== PackStatus.UNPUBLISHED && {
        key: 'unpublish',
        label: 'Unpublish',
        disabled: !canApplyPackStatusAction('unpublish', pkg.status, isLegacy),
        onClick: () => handlers.onUnpublish(pkg),
      },
      pkg.status !== PackStatus.HIDDEN && {
        key: 'hide',
        label: 'Hide',
        disabled: !canApplyPackStatusAction('hide', pkg.status, isLegacy),
        onClick: () => handlers.onHide(pkg),
      },
    ].filter(Boolean) as EntityRowConfig['actions'],
  };
};

export const PackagesTable = ({
  daytimePackages,
  nightStayPackages,
  isLegacy,
  onCreatePackage,
  onPublish,
  onSetup,
  onUnpublish,
  onHide,
  onReorder,
  activities,
  rooms,
}: Props) => {
  const handlers = useMemo(
    () => ({ onPublish, onSetup, onUnpublish, onHide }),
    [onPublish, onSetup, onUnpublish, onHide]
  );

  const allPackagesEmpty =
    nightStayPackages.length === 0 && daytimePackages.length === 0;

  const sections: EntitySectionConfig<HotelPackage>[] = allPackagesEmpty
    ? [{ key: 'all-packages', items: [] }]
    : [
        { key: 'night-stay', label: 'Night stay', items: nightStayPackages },
        { key: 'daytime', label: 'Daytime', items: daytimePackages },
      ];

  const renderSection = (section: EntitySectionConfig<HotelPackage>) => {
    const rowConfigGetter = (pkg: HotelPackage) =>
      getRowConfig(pkg, isLegacy, handlers, activities, rooms);

    const emptyLabel = allPackagesEmpty
      ? 'No packages'
      : `No ${section.label?.toLowerCase()} packages`;

    if (onReorder) {
      return (
        <SortableSection
          key={section.key}
          section={section}
          getId={(pkg) => pkg.id}
          renderItem={(pkg, dragHandleProps) => (
            <EntityRow
              tableType="package"
              config={rowConfigGetter(pkg)}
              dragHandleProps={dragHandleProps}
            />
          )}
          onReorder={onReorder}
          emptyLabel={emptyLabel}
          emptyDescription="No packages have been created for this hotel yet."
        />
      );
    }

    return (
      <EntitySection
        key={section.key}
        section={section}
        renderItem={(pkg) => {
          const config = rowConfigGetter(pkg);

          return (
            <EntityRow tableType="package" key={config.id} config={config} />
          );
        }}
        emptyLabel={emptyLabel}
        emptyDescription="No packages have been created for this hotel yet."
      />
    );
  };

  return (
    <EntityTable
      title="Packages"
      createLabel="Create new package"
      sections={sections}
      renderSection={renderSection}
      onCreate={onCreatePackage}
    />
  );
};
